import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { Article, FeedArticle, FeedCategory, FeedItem } from '@pff-consumer/schema'
import { includes, reject, without } from 'lodash'
import { consumerApi } from '../consumer-api/consumer-api'

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

export const FEED_KEY = 'feed'

interface ArticleFeedState {
  byId: {
    [key: string]: Article // byId is keyed by the Article ID and contains Article data
  }
  ids: Article['id'][] // list of all Article IDs
  byFeedCategory: {
    [key: string]: Article['id'][] // byFeedCategory is keyed by FeedCategory slug and contains a list of Article IDs
  }
}

interface TransformedFeedArticles {
  byId: {
    [key: string]: Article // byId is keyed by the Article ID and contains Article data
  }
  ids: Article['id'][]
}

interface ArticleIdsWithTimeStamp {
  id: Article['id']
  savedTimeStamp: number
}

interface ArticleBookmarkFeedState {
  bookmarkArticle: FeedArticle[]
  articleIds: ArticleIdsWithTimeStamp[]
  unreadArticles: Article['id'][]
}

type FeedCategoriesState = FeedCategory[]

export interface FeedState {
  articles: ArticleFeedState
  feedCategories: FeedCategoriesState
  filterCategory: FeedCategory
  feedBookmark: ArticleBookmarkFeedState
}

export const feedAdapter = createEntityAdapter<FeedState>()

export const initialFeedState: FeedState = feedAdapter.getInitialState({
  articles: {
    byId: {},
    ids: [],
    byFeedCategory: {},
  },
  feedCategories: [],
  filterCategory: {
    slug: 'all',
    displayName: 'All',
    order: 0,
  },
  feedBookmark: {
    bookmarkArticle: [],
    articleIds: [],
    unreadArticles: [],
  },
})

const transformFeedArticles = (articles: FeedArticle[]): TransformedFeedArticles => {
  return {
    byId: articles.reduce(
      (articlesById, article) => ({
        ...articlesById,
        [article.id]: article,
      }),
      {}
    ),
    ids: articles.map((article) => article.id),
  }
}

export const feedSlice = createSlice({
  name: FEED_KEY,
  initialState: initialFeedState,
  reducers: {
    addBookmarkedArticleId: (state, { payload }) => {
      const { id } = payload
      const bookmarkArticleObject = { id, savedTimeStamp: new Date().getTime() }
      const copiedArticleIds = [...state.feedBookmark.articleIds]
      copiedArticleIds.unshift(bookmarkArticleObject)
      state.feedBookmark = {
        ...state.feedBookmark,
        articleIds: copiedArticleIds,
        unreadArticles: [...state.feedBookmark.unreadArticles, id],
      }
    },
    addBookmarkedArticles: (state, { payload }: { payload: { bookmarkedArticles: FeedArticle[] } }) => {
      const { bookmarkedArticles } = payload
      const ids = state.feedBookmark.articleIds.map((a) => a?.id)
      bookmarkedArticles.sort((a, b) => ids.indexOf(a?.id) - ids.indexOf(b?.id))
      state.feedBookmark = { ...state.feedBookmark, bookmarkArticle: [...bookmarkedArticles] }
    },
    removeBookmarkedArticle: (state, { payload }: { payload: { id: number } }) => {
      const { id } = payload
      const { bookmarkArticle, articleIds, unreadArticles } = { ...state.feedBookmark }
      const bookmarkedArticle = reject([...bookmarkArticle], ['id', id])
      const bookmarkArticleIds = reject([...articleIds], ['id', id])
      const updatedUnreadArticlesList = includes(unreadArticles, id) ? without(unreadArticles, id) : [...unreadArticles]

      state.feedBookmark = {
        ...state.feedBookmark,
        unreadArticles: updatedUnreadArticlesList,
        bookmarkArticle: bookmarkedArticle,
        articleIds: bookmarkArticleIds,
      }
    },
    updateUnreadArticlesList: (state) => {
      state.feedBookmark = { ...state.feedBookmark, unreadArticles: [] }
    },
    addFilterCategory: (state, { payload }) => {
      state.filterCategory = payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(consumerApi.endpoints.getFeedItems.matchFulfilled, (state, { payload }: { payload: FeedItem[] }) => {
        const articles = payload.filter((feedItem) => feedItem.type === 'article')
        const transformedArticles = transformFeedArticles(articles)

        let byFeedCategory: ArticleFeedState['byFeedCategory'] = {
          ...state.articles.byFeedCategory,
        }

        articles.forEach((article: FeedArticle) => {
          if (article?.feedCategories) {
            article?.feedCategories.forEach((feedCategory: string) => {
              const existingArticleIdsInFeedCategory = byFeedCategory[feedCategory] ?? []

              if (!existingArticleIdsInFeedCategory.includes(article.id)) {
                byFeedCategory = {
                  ...byFeedCategory,
                  [feedCategory]: [...existingArticleIdsInFeedCategory, article.id],
                }
              }
            })
          }
        })

        const articleIds = Object.keys(state.articles.byId)

        if (articleIds.length > 0) {
          articleIds.forEach((articleId) => {
            const transformedArticle = transformedArticles.byId[articleId]
            if (transformedArticle) {
              transformedArticles.byId[articleId] = { ...state.articles.byId[articleId], ...transformedArticle }
            } else {
              transformedArticles.byId[articleId] = { ...state.articles.byId[articleId] }
            }
          })
        }

        state.articles = {
          ...state.articles,
          byId: {
            ...state.articles.byId,
            ...transformedArticles.byId,
          },
          ids: [...state.articles.ids, ...transformedArticles.ids],
          byFeedCategory,
        }
      })
      .addMatcher(
        consumerApi.endpoints.getArticleByIdOrSlug.matchFulfilled,
        (state, { payload }: { payload: Article | null }) => {
          if (payload != null) {
            state.articles = {
              ...state.articles,
              byId: {
                ...state.articles.byId,
                [payload.id]: {
                  ...(state.articles.byId[payload.id] ?? {}),
                  ...payload,
                },
              },
            }
          }
        }
      )
      .addMatcher(
        consumerApi.endpoints.getFeedCategories.matchFulfilled,
        (state, { payload }: { payload: FeedCategory[] }) => {
          state.feedCategories = payload
        }
      )
  },
})

export const feedReducer = feedSlice.reducer
export const feedActions = feedSlice.actions

export const getFeedState = (rootState: { [x: string]: FeedState }): FeedState => {
  return rootState[FEED_KEY]
}

const byPublishedTimestamp = (a: Article, b: Article) => {
  if (a.publishedTimestamp && b.publishedTimestamp) {
    if (a.publishedTimestamp > b.publishedTimestamp) return -1
    if (a.publishedTimestamp < b.publishedTimestamp) return 1
  }
  return 0
}

const getAllArticles = (articleFeedState: ArticleFeedState) =>
  Object.entries(articleFeedState.byId)
    .map(([_, value]) => value)
    .filter((article) => !!article?.publishedTimestamp)
    .sort(byPublishedTimestamp)

const transformArticlesToFeedArticles = (articles: Article[]): FeedArticle[] =>
  articles.map((article) => ({
    authors: article.authors,
    feedCategories: article.feedCategories,
    featuredImage: article.featuredImage,
    id: article.id,
    metaSlug: article.metaSlug,
    isPremiumContent: article.isPremiumContent,
    publishedTimestamp: article.publishedTimestamp,
    title: article.title,
    url: article.url,
    type: 'article',
  }))

const getArticleIdsByFeedCategory = (
  articleFeedState: ArticleFeedState,
  feedCategorySlug: FeedCategory['slug']
): Article['id'][] => articleFeedState.byFeedCategory[feedCategorySlug] ?? []

const getArticlesByIds = (articleFeedState: ArticleFeedState, articleIds: Article['id'][]): Article[] =>
  articleIds
    .map((articleId) => articleFeedState.byId[articleId] ?? null)
    .filter(notEmpty)
    .sort(byPublishedTimestamp)

const getArticleByIdSelector = (state: FeedState, articleId: Article['id']) => state.articles.byId[articleId]

const getBookmarkArticleIds = (state: FeedState) => state.feedBookmark.articleIds.map((a) => a?.id)

export const feedSelectors = {
  getFeedCategories: createSelector(getFeedState, (state: FeedState) => state.feedCategories),
  getFeedArticlesByFeedCategory: (feedCategorySlug: FeedCategory['slug']) =>
    createSelector(getFeedState, (state: FeedState) =>
      transformArticlesToFeedArticles(
        getArticlesByIds(state.articles, getArticleIdsByFeedCategory(state.articles, feedCategorySlug))
      )
    ),
  getFeedArticles: createSelector(getFeedState, (state: FeedState) =>
    transformArticlesToFeedArticles(getAllArticles(state.articles))
  ),
  getBookmarkFeedArticles: createSelector(getFeedState, (state: FeedState) => state.feedBookmark.bookmarkArticle),
  getBookmarkArticleIds: createSelector(getFeedState, (state: FeedState) => getBookmarkArticleIds(state)),
  getArticles: createSelector(getFeedState, (state: FeedState) => getAllArticles(state.articles)),
  getArticleIds: createSelector(getFeedState, (state: FeedState) => state.articles.ids),
  getFilterCategory: createSelector(getFeedState, (state: FeedState) => state.filterCategory),
  getFeedArticleById: (articleId: Article['id']) =>
    createSelector(getFeedState, (state: FeedState) => getArticleByIdSelector(state, articleId)),
  getUnreadArticlesList: createSelector(getFeedState, (state: FeedState) => state.feedBookmark.unreadArticles),
}
